/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { Calendar, Views } from 'react-big-calendar';
import type { EventProps, SlotInfo, View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { momentLocalizer } from 'react-big-calendar';
import { FolderSymlinkFill } from 'react-bootstrap-icons';
import moment from 'moment';

const DnDCalendar = withDragAndDrop(Calendar);

const localizer = momentLocalizer(moment);

const messages = {
  today: 'Dzisiaj',
  previous: 'Poprzedni',
  next: 'Następny',
  month: 'Miesiąc',
  week: 'Tydzień',
  day: 'Dzień',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Czas',
  event: 'Wydarzenie',
  noEventsInRange: 'Brak wydarzeń w tym zakresie.',
};

export interface ResourceVisit {
  resourceId: string;
  healthCareFacilityName: string;
  resourceTitle: string;
}

export interface EventVisit {
  title?: string | null;
  start?: Date | null;
  end?: Date | null;
  resourceId: string;
  finished: boolean;
  external: boolean;
}

type BigCalendarProps = React.PropsWithChildren<{
  handleEventClick: (e: any) => void;
  handleEventDrop: (e: any) => void;
  handleEventResize: (e: any) => void;
  handleAddEvent?: (slotInfo: SlotInfo) => void;
  handleRangeChange: (range: Date[] | { start: Date; end: Date }) => void;
  events: EventVisit[];
  resources: ResourceVisit[];
  date?: Date;
  view?: View;
  onNavigate?: (date: Date) => void;
  onView?: (view: View) => void;
  draggableAccessor?: (event: EventVisit) => boolean;
  selectable?: boolean | 'ignoreEvents';
  disableAllDaySlotSelection?: boolean;
}>;

const isAllDaySlot = (slotInfo: SlotInfo) => {
  return slotInfo.slots?.every(
    date => date.getHours() === 0 && date.getMinutes() === 0,
  );
};

export const BigCalendar: React.FC<BigCalendarProps> = props => {
  const [resourcesView, setResourcesView] = useState(false);
  const calendarRef = useRef<Calendar>(null);
  const pointerStartedOnEvent = useRef(false);

  // react-big-calendar rozstrzyga „czy kliknięto w wydarzenie" przez
  // document.elementFromPoint dopiero w momencie obsługi kliknięcia. Jeśli
  // onSelectEvent zdążył otworzyć modal, to jego backdrop zasłania kalendarz,
  // test wypada negatywnie i obok onSelectEvent leci jeszcze onSelectSlot.
  // Dlatego zapamiętujemy trafienie w wydarzenie już przy wciśnięciu przycisku.
  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = (
        'touches' in event ? event.touches[0]?.target : event.target
      ) as HTMLElement | null;
      pointerStartedOnEvent.current = !!target?.closest?.('.rbc-event');
    };

    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown, true);
    };
  }, []);

  const isDayOrAgendaView = (view: string) => {
    return view === 'day' || view === 'agenda';
  };

  const eventPropGetter = (event: EventVisit) => {
    if (event.finished) {
      return {
        style: {
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb',
          fontWeight: '500',
        },
      };
    }

    return {
      style: {
        backgroundColor: '#cfe2ff',
        color: '#084298',
        border: '1px solid #b6d4fe',
      },
    };
  };

  const CustomAgendaEvent: React.FC<EventProps<object>> = ({ event }) => {
    const customEvent = event as unknown as EventVisit; // Rzutowanie na ResourceVisit

    return (
      <span>
        {customEvent.finished && '✅ '}
        {customEvent.external && (
          <FolderSymlinkFill className="me-2 mb-1 icon-blue" size={16} />
        )}
        {customEvent.title?.includes('💰') && '💰 '}
        <strong>{customEvent.title?.replace('💰', '').trim()}</strong>
        {' - '}
        {customEvent.resourceId
          ? props.resources
              .filter(o => o.resourceId === customEvent.resourceId)
              .map(o => o.resourceTitle)
          : ''}
      </span>
    );
  };

  const CustomEvent: React.FC<EventProps<object>> = ({ event }) => {
    const customEvent = event as unknown as EventVisit;

    return (
      <div style={{ padding: '2px 4px' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>
          {customEvent.finished && '✅ '}
          {customEvent.external && (
            <FolderSymlinkFill className="me-2 mb-1 icon-blue" size={16} />
          )}
          {customEvent.title}
        </div>
      </div>
    );
  };

  return (
    <DnDCalendar
      localizer={localizer}
      events={props.events}
      draggableAccessor={event =>
        props.draggableAccessor
          ? props.draggableAccessor(event as EventVisit)
          : true
      }
      className="full-height-calendar"
      step={10}
      timeslots={2}
      min={new Date(2024, 0, 0, 7, 0)}
      max={new Date(2124, 0, 0, 20, 0)}
      defaultView={Views.WEEK}
      views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
      messages={messages}
      ref={calendarRef}
      selectable={props.selectable ?? true}
      onSelectSlot={slotInfo => {
        if (pointerStartedOnEvent.current) {
          return;
        }
        if (props.disableAllDaySlotSelection && isAllDaySlot(slotInfo)) {
          return;
        }
        props.handleAddEvent?.(slotInfo);
      }}
      onSelectEvent={props.handleEventClick}
      onEventDrop={props.handleEventDrop}
      onEventResize={props.handleEventResize}
      date={props.date}
      view={props.view}
      onNavigate={props.onNavigate}
      onView={view => {
        if (isDayOrAgendaView(view)) {
          setResourcesView(true);
        } else {
          setResourcesView(false);
        }
        props.onView?.(view);
      }}
      resources={
        resourcesView
          ? props.resources.sort((a, b) =>
              (a.healthCareFacilityName + a.resourceTitle || '').localeCompare(
                b.healthCareFacilityName + b.resourceTitle || '',
              ),
            )
          : undefined
      }
      resourceTitleAccessor={resource =>
        (resource as ResourceVisit).resourceTitle
      }
      resourceIdAccessor={resource => (resource as ResourceVisit).resourceId}
      eventPropGetter={event => eventPropGetter(event as EventVisit)}
      // resourceGroupingLayout={groupResourcesOnWeek}
      components={{
        agenda: {
          event: CustomAgendaEvent as any,
        },
        event: CustomEvent as any,
      }}
      onRangeChange={props.handleRangeChange}
    />
  );
};
